import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertTriangle, User as UserIcon, Calendar as CalIcon } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { api, formatApiError } from "@/lib/api";
import { JOB_STATUS_FLOW, JOB_STATUS_LABELS, PRIORITY_LABELS, jobStatusTone } from "@/lib/constants";
import { formatDate } from "@/lib/format";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";

/**
 * Kanban board with drag-and-drop.
 * Data is server-side; each column loads up to N (default 50) cards.
 */
export default function KanbanPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salespersonId, setSalespersonId] = useState("");
  const [productCategoryId, setProductCategoryId] = useState("");
  const [district, setDistrict] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [salespeople, setSalespeople] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [dragging, setDragging] = useState(null); // {id, version, from}
  const [dropTarget, setDropTarget] = useState(null);

  // Scroll synchronization refs and states
  const boardRef = useRef(null);
  const stickyScrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const isSyncingBoard = useRef(false);
  const isSyncingSticky = useRef(false);

  const [boardScrollWidth, setBoardScrollWidth] = useState(0);
  const [boardClientWidth, setBoardClientWidth] = useState(0);
  const [isSentinelVisible, setIsSentinelVisible] = useState(false);

  const loadRefs = useCallback(async () => {
    try {
      const [pcs, sps] = await Promise.all([
        api.get("/product-categories"),
        hasRole(user, "admin") ? api.get("/users/salespeople") : Promise.resolve({ data: [] }),
      ]);
      setProductCategories(pcs.data);
      setSalespeople(sps.data);
    } catch (_) {}
  }, [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { per_column_limit: 50 };
      if (salespersonId) params.salesperson_id = salespersonId;
      if (productCategoryId) params.product_category_id = productCategoryId;
      if (district) params.district = district;
      const r = await api.get("/jobs/kanban", { params });
      setBoard(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [salespersonId, productCategoryId, district]);

  useEffect(() => { loadRefs(); }, [loadRefs]);
  useEffect(() => { load(); }, [load]);

  const visibleColumns = useMemo(() => {
    if (!board) return [];
    if (!statusFilter) return board.columns;
    return board.columns.filter((c) => c.status === statusFilter);
  }, [board, statusFilter]);

  const handleDragStart = (e, card, fromStatus) => {
    if (isMobile) return;
    setDragging({ id: card.id, version: card.version, from: fromStatus, jobNumber: card.job_number });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", card.id); } catch (_) {}
  };

  const handleDragOver = (e, toStatus) => {
    if (isMobile) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropTarget !== toStatus) setDropTarget(toStatus);
  };

  const handleDragLeave = () => {
    if (isMobile) return;
    setDropTarget(null);
  };

  const handleDrop = async (e, toStatus) => {
    if (isMobile) return;
    e.preventDefault();
    setDropTarget(null);
    if (!dragging) return;
    if (dragging.from === toStatus) { setDragging(null); return; }
    try {
      await api.post(`/jobs/${dragging.id}/status`, {
        new_status: toStatus,
        explanation: `Kanban ile ${JOB_STATUS_LABELS[dragging.from]} → ${JOB_STATUS_LABELS[toStatus]} taşındı.`,
        version: dragging.version,
      });
      toast.success(`${dragging.jobNumber}: ${JOB_STATUS_LABELS[toStatus]}`);
      setDragging(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setDragging(null);
      load();
    }
  };

  // Scroll Synchronization Helpers
  const handleBoardScroll = (e) => {
    if (isSyncingBoard.current) {
      isSyncingBoard.current = false;
      return;
    }
    if (stickyScrollRef.current) {
      isSyncingSticky.current = true;
      stickyScrollRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const handleStickyScroll = (e) => {
    if (isSyncingSticky.current) {
      isSyncingSticky.current = false;
      return;
    }
    if (boardRef.current) {
      isSyncingBoard.current = true;
      boardRef.current.scrollLeft = e.target.scrollLeft;
    }
  };

  const updateScrollWidths = useCallback(() => {
    if (boardRef.current) {
      setBoardScrollWidth(boardRef.current.scrollWidth);
      setBoardClientWidth(boardRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    if (!loading && board) {
      const timer = setTimeout(() => {
        updateScrollWidths();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, board, visibleColumns, updateScrollWidths]);

  useEffect(() => {
    window.addEventListener("resize", updateScrollWidths);
    return () => window.removeEventListener("resize", updateScrollWidths);
  }, [updateScrollWidths]);

  // Sentinel IntersectionObserver
  useEffect(() => {
    if (loading || !board) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSentinelVisible(entry.isIntersecting);
      },
      { threshold: 0 }
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [loading, board]);

  const showSticky = boardScrollWidth > boardClientWidth && !isSentinelVisible;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="kanban-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">Operasyon Kanban</h1>
          <p className="text-xs text-slate-500 mt-1">İşleri sürükleyerek durum değiştirin. Her hareket denetim kaydına yazılır.</p>
        </div>
      </div>
      <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 grid gap-3 md:grid-cols-5 shadow-sm">
        <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="border-[#DCE2E8]" data-testid="kanban-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent className="bg-white border-[#DCE2E8]">
            <SelectItem value="__all__">Tüm Durumlar</SelectItem>
            {JOB_STATUS_FLOW.map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={productCategoryId || "__all__"} onValueChange={(v) => setProductCategoryId(v === "__all__" ? "" : v)}>
          <SelectTrigger className="border-[#DCE2E8]" data-testid="kanban-product-filter"><SelectValue placeholder="Ürün Grubu" /></SelectTrigger>
          <SelectContent className="bg-white border-[#DCE2E8]">
            <SelectItem value="__all__">Tüm Ürünler</SelectItem>
            {productCategories.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
          </SelectContent>
        </Select>
        {hasRole(user, "admin") && (
          <Select value={salespersonId || "__all__"} onValueChange={(v) => setSalespersonId(v === "__all__" ? "" : v)}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="kanban-salesperson-filter"><SelectValue placeholder="Satış Personeli" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Personel</SelectItem>
              {salespeople.map((sp) => (<SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        <Input placeholder="İlçe" value={district} onChange={(e) => setDistrict(e.target.value)} onBlur={load} className="border-[#DCE2E8] focus-visible:ring-[#FF353A]" data-testid="kanban-district-filter" />
        <Button variant="secondary" className="bg-[#F4F6F8] hover:bg-slate-200 text-[#171A1F] border border-[#DCE2E8]" onClick={load} data-testid="kanban-refresh">Yenile</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500"><Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...</div>
      ) : (
        <div className="relative">
          <div
            ref={boardRef}
            onScroll={handleBoardScroll}
            className="flex gap-3 overflow-x-auto pb-4 scrollbar-thin snap-x snap-mandatory max-w-full"
            data-testid="kanban-board"
          >
            {visibleColumns.map((col) => (
              <div
                key={col.status}
                onDragOver={(e) => handleDragOver(e, col.status)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col.status)}
                className={`min-w-[280px] w-[280px] sm:min-w-[300px] sm:w-[300px] shrink-0 snap-center rounded-md border ${dropTarget === col.status ? "border-[#FF353A] bg-[#FFF0F0]/50" : "border-[#DCE2E8] bg-[#F4F6F8]"} transition-colors`}
                data-testid={`kanban-column-${col.status}`}
              >
                <div className="px-3 py-2 border-b border-[#DCE2E8] flex items-center justify-between bg-white rounded-t-md">
                  <div className="flex items-center gap-2">
                    <StatusBadge tone={jobStatusTone(col.status)}>{JOB_STATUS_LABELS[col.status]}</StatusBadge>
                  </div>
                  <span className="text-xs text-slate-500 font-mono font-medium">
                    {col.loaded}<span className="text-slate-400">/{col.total}</span>
                  </span>
                </div>
                <div className="p-2 space-y-2 min-h-[80px]">
                  {col.items.map((c) => (
                    <div
                      key={c.id}
                      draggable={!isMobile}
                      onDragStart={(e) => handleDragStart(e, c, col.status)}
                      onClick={() => navigate(`/isler/${c.id}`)}
                      className={`rounded-md border border-[#DCE2E8] bg-white p-3 ${isMobile ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} hover:border-[#168DEB] hover:shadow-sm transition-all space-y-2`}
                      data-testid={`kanban-card-${c.job_number}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-semibold text-[#FF353A]">{c.job_number}</span>
                        {c.is_installation_delayed && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 text-[10px] font-medium">
                            <AlertTriangle size={10} /> Montaj Gecikmiş
                          </span>
                        )}
                      </div>
                      <div className="text-sm font-medium text-[#171A1F] leading-tight">{c.customer_name}</div>
                      {c.product_group_names?.length > 0 && (
                        <div className="text-xs text-slate-500 leading-tight">{c.product_group_names.join(" · ")}</div>
                      )}
                      <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                        <span className="inline-flex items-center gap-1"><UserIcon size={11} />{c.salesperson_name || "—"}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium ${
                          c.priority === "acil" ? "bg-rose-100 text-rose-800" :
                          c.priority === "yuksek" ? "bg-amber-100 text-amber-800" :
                          c.priority === "dusuk" ? "bg-slate-100 text-slate-600" :
                          "bg-[#EAF7FF] text-[#168DEB]"
                        }`}>{PRIORITY_LABELS[c.priority]}</span>
                      </div>
                      {c.planned_installation_start && (
                        <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                          <CalIcon size={11} /> {formatDate(c.planned_installation_start)}
                        </div>
                      )}
                    </div>
                  ))}
                  {col.items.length === 0 && (
                    <div className="text-xs text-slate-400 text-center py-6">Bu durumda kayıt yok</div>
                  )}
                  {col.total > col.loaded && (
                    <div className="text-xs text-slate-500 text-center py-2 border-t border-[#DCE2E8] mt-2">
                      +{col.total - col.loaded} kayıt daha var (filtre daraltın)
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div ref={sentinelRef} className="h-2" />

          {showSticky && (
            <div
              ref={stickyScrollRef}
              onScroll={handleStickyScroll}
              className="sticky bottom-0 z-20 w-full overflow-x-auto bg-white/90 backdrop-blur-sm border-t border-[#DCE2E8] py-2 shadow-sm scrollbar-thin"
              data-testid="kanban-sticky-scrollbar"
            >
              <div style={{ width: `${boardScrollWidth}px`, height: "1px" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
